import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import { ActionBackOfficeResponse } from '../../../../core/models/api.model';
import { BankAccount, BankAccountPayload } from '../../../../core/models/finance/bank-account.model';
import { ApiBankAccountService } from '../../../../core/services/api/finance/api-bank-account.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';

interface BankAccountForm {
  id: number;
  name: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
}

const EMPTY_FORM: BankAccountForm = {
  id: 0,
  name: '',
  bankName: '',
  accountName: '',
  accountNumber: '',
  accountType: '',
};

@Component({
  selector: 'app-bank-accounts',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './bank-accounts.component.html',
})
export class BankAccountsComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiBankAccountService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly bankNameOptions = [
    'ธนาคารกสิกรไทย',
    'ธนาคารไทยพาณิชย์',
    'ธนาคารกรุงเทพ',
    'ธนาคารกรุงไทย',
    'ธนาคารกรุงศรีอยุธยา',
    'ธนาคารทหารไทยธนชาต',
    'ธนาคารออมสิน',
    'ธนาคารยูโอบี',
    'ธนาคารซีไอเอ็มบี ไทย',
    'ธนาคารแลนด์ แอนด์ เฮ้าส์',
    'ธนาคารเกียรตินาคินภัทร',
    'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร',
  ];
  readonly accountTypeOptions = ['ออมทรัพย์', 'กระแสรายวัน', 'ฝากประจำ'];

  accounts = signal<BankAccount[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  statusFilter = signal<'active' | 'inactive' | 'all'>('active');
  searchText = signal('');

  readonly visibleAccounts = computed(() => {
    const keyword = this.searchText().trim().toLowerCase();
    const status = this.statusFilter();
    return this.accounts()
      .filter((a) => status === 'all' || (status === 'active' ? a.active : !a.active))
      .filter((a) =>
        !keyword ||
        [a.name, a.bankName, a.accountName, a.accountNumber].some((v) => v.toLowerCase().includes(keyword)),
      );
  });
  readonly activeCount = computed(() => this.accounts().filter((a) => a.active).length);
  readonly inactiveCount = computed(() => this.accounts().filter((a) => !a.active).length);

  isFormOpen = false;
  form: BankAccountForm = { ...EMPTY_FORM };
  isBankDropdownOpen = false;

  toggleTarget: BankAccount | null = null;
  historyTarget: BankAccount | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => this.loadAccounts());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadAccounts();
  }

  loadAccounts(): void {
    const siteId = this.selectedSiteId;
    const userId = this.resolveUserId();
    if (!siteId || !userId) {
      this.accounts.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api.getBankAccountList(siteId, userId, true).subscribe({
      next: (rows) => {
        this.accounts.set(rows);
        this.isLoading.set(false);
      },
      error: () => {
        this.accounts.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดบัญชีธนาคารไม่สำเร็จ');
      },
    });
  }

  openCreate(): void {
    if (!this.canCreate) return;
    this.form = { ...EMPTY_FORM };
    this.isFormOpen = true;
  }

  openEdit(account: BankAccount): void {
    if (!this.canUpdate) return;
    this.form = {
      id: account.id,
      name: account.name,
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
    };
    this.isFormOpen = true;
  }

  closeForm(): void {
    if (this.isSaving()) return;
    this.isFormOpen = false;
    this.isBankDropdownOpen = false;
  }

  onRefresh(): void {
    this.searchText.set('');
    this.statusFilter.set('active');
    this.loadAccounts();
  }

  get filteredBankOptions(): string[] {
    const keyword = this.form.bankName.trim().toLowerCase();
    if (!keyword) return this.bankNameOptions;
    return this.bankNameOptions.filter((b) => b.toLowerCase().includes(keyword));
  }

  selectBank(bankName: string): void {
    const previousBank = this.form.bankName;
    this.form.bankName = bankName;
    if (!this.form.name.trim() || this.form.name === previousBank) {
      this.form.name = bankName;
    }
    this.isBankDropdownOpen = false;
  }

  closeBankDropdown(): void {
    this.isBankDropdownOpen = false;
  }

  selectAccountType(type: string): void {
    this.form.accountType = this.form.accountType === type ? '' : type;
  }

  saveForm(): void {
    const f = this.form;
    if (!f.name.trim() || !f.bankName.trim() || !f.accountName.trim() || !f.accountNumber.trim()) {
      this.toast.error('กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบ');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload: BankAccountPayload = {
      userId,
      menuId,
      siteId: this.selectedSiteId,
      name: f.name.trim(),
      bankName: f.bankName.trim(),
      accountName: f.accountName.trim(),
      accountNumber: f.accountNumber.trim(),
      accountType: f.accountType.trim() || null,
    };

    const request$ = f.id
      ? this.api.updateBankAccount(f.id, payload)
      : this.api.insertBankAccount(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (this.handleResult(res, f.id ? 'แก้ไขบัญชีเรียบร้อย' : 'เพิ่มบัญชีเรียบร้อย')) {
          this.isFormOpen = false;
          this.loadAccounts();
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      },
    });
  }

  askToggleActive(account: BankAccount): void {
    if (account.active ? !this.canDelete : !this.canUpdate) return;
    this.toggleTarget = account;
  }

  confirmToggleActive(): void {
    const target = this.toggleTarget;
    if (!target) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isSaving.set(true);
    this.api.setBankAccountActive(target.id, { userId, menuId, active: !target.active }).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        this.toggleTarget = null;
        if (this.handleResult(res, target.active ? 'ปิดใช้งานบัญชีเรียบร้อย' : 'เปิดใช้งานบัญชีเรียบร้อย')) {
          this.loadAccounts();
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      },
    });
  }

  openHistory(account: BankAccount): void {
    this.historyTarget = account;
  }

  openFormHistory(): void {
    const account = this.accounts().find((a) => a.id === this.form.id);
    if (account) this.historyTarget = account;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private handleResult(res: ActionBackOfficeResponse, fallbackSuccess: string): boolean {
    const raw = res as ActionBackOfficeResponse & Record<string, unknown>;
    const isSuccess = Boolean(raw.isSuccess ?? raw['IsSuccess']);
    const message = String(raw.message ?? raw['Message'] ?? '');
    if (isSuccess) {
      this.toast.success(message || fallbackSuccess);
    } else {
      this.toast.error(message || 'บันทึกไม่สำเร็จ');
    }
    return isSuccess;
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
